import {
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import type {
  VectorDroneConfig,
  VectorDroneInstance,
  VectorDroneSubmission,
} from "../../puzzles/core/vectorDrone";
import {
  normalizeVectorDroneDifficulty,
  resolveVectorDroneConfig,
  vectorDroneDifficultyLabels,
} from "../../puzzles/core/vectorDrone";
import { Button, Card, Pill } from "../ui";
import { Icon } from "../Icon";

type Point = { x: number; y: number };

const origin = { x: 95, y: 290 };
const scale = 2.1;

const difficultyTools = {
  practice: {
    coordinates: true,
    gridLabels: true,
    componentTriangle: true,
    componentLabels: true,
    magnitude: true,
    angle: true,
    formula: true,
    ghost: true,
    ruler: true,
    protractor: true,
    hover: true,
    draggable: true,
    manualEntry: true,
    showBeforeSubmit: true,
  },
  "insert-coin": {
    coordinates: true,
    gridLabels: true,
    componentTriangle: true,
    componentLabels: true,
    magnitude: true,
    angle: false,
    formula: true,
    ghost: true,
    ruler: false,
    protractor: false,
    hover: false,
    draggable: true,
    manualEntry: true,
    showBeforeSubmit: true,
  },
  "level-up": {
    coordinates: true,
    gridLabels: false,
    componentTriangle: false,
    componentLabels: false,
    magnitude: false,
    angle: false,
    formula: false,
    ghost: false,
    ruler: false,
    protractor: false,
    hover: false,
    draggable: true,
    manualEntry: true,
    showBeforeSubmit: true,
  },
  "developer-mode": {
    coordinates: false,
    gridLabels: false,
    componentTriangle: false,
    componentLabels: false,
    magnitude: false,
    angle: false,
    formula: false,
    ghost: false,
    ruler: false,
    protractor: false,
    hover: false,
    draggable: false,
    manualEntry: true,
    showBeforeSubmit: false,
  },
} as const;

export function VectorDronePlayer({
  config,
  instance,
  onLock,
}: {
  config: VectorDroneConfig;
  instance: VectorDroneInstance;
  onLock(submission: VectorDroneSubmission): void;
}) {
  const resolvedConfig = useMemo(() => resolveVectorDroneConfig(config), [config]);
  const difficulty = normalizeVectorDroneDifficulty(resolvedConfig.difficulty);
  const tools = difficultyTools[difficulty];
  const ideal = useMemo(
    () => ({
      x: instance.target.x - instance.wind.x,
      y: instance.target.y - instance.wind.y,
    }),
    [instance],
  );
  const [x, setX] = useState(Math.round(ideal.x * 0.72));
  const [y, setY] = useState(Math.round(ideal.y * 0.72));
  const [simulationProgress, setSimulationProgress] = useState<number | null>(
    null,
  );
  const [submitted, setSubmitted] = useState(false);
  const [hoverPoint, setHoverPoint] = useState<Point | null>(null);

  useEffect(() => {
    setX(Math.round(ideal.x * 0.72));
    setY(Math.round(ideal.y * 0.72));
    setSubmitted(false);
    setHoverPoint(null);
  }, [ideal]);

  const resultant = { x: x + instance.wind.x, y: y + instance.wind.y };
  const inputMagnitude = Math.hypot(x, y);
  const inputAngle = normalizeDegrees((Math.atan2(y, x) * 180) / Math.PI);
  const missDistance = Math.hypot(
    resultant.x - instance.target.x,
    resultant.y - instance.target.y,
  );
  const showVector = tools.showBeforeSubmit || submitted || simulationProgress !== null;
  const propulsionTip = toSvg({ x, y });
  const landing = toSvg(resultant);
  const target = toSvg(instance.target);
  const isSimulating = simulationProgress !== null;
  const dronePosition =
    simulationProgress === null
      ? showVector
        ? landing
        : toSvg({ x: 0, y: 0 })
      : {
          x: origin.x + (landing.x - origin.x) * simulationProgress,
          y: origin.y + (landing.y - origin.y) * simulationProgress,
        };

  const updateVector = (next: Point) => {
    if (!isFinitePoint(next)) return;
    setX(clampInput(Math.round(next.x), resolvedConfig));
    setY(clampInput(Math.round(next.y), resolvedConfig));
  };

  const lockSubmission = () => {
    if (isSimulating) return;
    setSubmitted(true);
    runSimulation(setSimulationProgress, () => onLock({ x, y }));
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[.66fr_1.34fr]">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-xl font-bold text-white">
            Propulsion console
          </h3>
          <Pill tone={difficulty === "developer-mode" ? "amber" : "emerald"}>
            {vectorDroneDifficultyLabels[difficulty]}
          </Pill>
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-400">
          Counter the wind vector so propulsion plus wind lands inside the
          extraction ring.
        </p>

        <div className="mt-5 grid gap-3">
          {tools.manualEntry && (
            <div className="grid gap-3 sm:grid-cols-2">
              <NumberControl
                label="X component"
                value={x}
                min={resolvedConfig.inputMin}
                max={resolvedConfig.inputMax}
                disabled={isSimulating}
                onChange={(value) => setX(clampInput(value, resolvedConfig))}
              />
              <NumberControl
                label="Y component"
                value={y}
                min={resolvedConfig.inputMin}
                max={resolvedConfig.inputMax}
                disabled={isSimulating}
                onChange={(value) => setY(clampInput(value, resolvedConfig))}
              />
            </div>
          )}

          {difficulty !== "developer-mode" && (
            <div className="space-y-5">
              <RangeControl
                label="Horizontal thrust"
                value={x}
                min={resolvedConfig.inputMin}
                max={resolvedConfig.inputMax}
                disabled={isSimulating}
                onChange={(value) => setX(clampInput(value, resolvedConfig))}
              />
              <RangeControl
                label="Vertical thrust"
                value={y}
                min={resolvedConfig.inputMin}
                max={resolvedConfig.inputMax}
                disabled={isSimulating}
                onChange={(value) => setY(clampInput(value, resolvedConfig))}
              />
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {tools.coordinates && (
            <VectorReadout
              label="Target"
              x={instance.target.x}
              y={instance.target.y}
              color="text-cyan-300"
            />
          )}
          <VectorReadout
            label="Wind"
            x={instance.wind.x}
            y={instance.wind.y}
            color="text-orange-300"
          />
          {showVector && (
            <VectorReadout
              label="Result"
              x={resultant.x}
              y={resultant.y}
              color="text-violet-300"
            />
          )}
          {(tools.magnitude || (submitted && difficulty === "level-up")) && (
            <ScalarReadout label="Magnitude" value={inputMagnitude.toFixed(1)} />
          )}
          {(tools.angle || (submitted && difficulty === "level-up")) && (
            <ScalarReadout label="Angle" value={`${inputAngle.toFixed(1)} deg`} />
          )}
          {submitted && (
            <ScalarReadout
              label="Miss distance"
              value={missDistance.toFixed(1)}
              tone={missDistance <= resolvedConfig.tolerance ? "text-emerald-300" : "text-amber-300"}
            />
          )}
        </div>

        {tools.formula && (
          <div className="mt-5 rounded-xl border border-slate-800 bg-slate-950/65 p-3">
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Formula panel
            </p>
            <p className="mt-2 font-mono text-xs text-slate-300">
              [{x}, {y}] + [{instance.wind.x}, {instance.wind.y}] = [
              {resultant.x}, {resultant.y}]
            </p>
          </div>
        )}

        <div className="mt-5 flex gap-2">
          {difficulty === "practice" && (
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              disabled={isSimulating}
              onClick={() => {
                setX(Math.round(ideal.x * 0.72));
                setY(Math.round(ideal.y * 0.72));
                setSubmitted(false);
              }}
            >
              Reset
            </Button>
          )}
          <Button className="flex-1" onClick={lockSubmission} disabled={isSimulating}>
            <Icon name="Navigation" size={18} />
            {isSimulating ? "Simulating vector..." : "Submit / simulate"}
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden p-3 sm:p-5">
        <div className="mb-3 flex items-center justify-between px-2">
          <span className="font-retro text-[8px] tracking-widest text-slate-400">
            VECTOR FLIGHT MONITOR
          </span>
          <span className="flex items-center gap-2 text-[10px] font-bold text-emerald-300">
            <span className="h-2 w-2 animate-pulse-soft rounded-full bg-emerald-300" />
            {tools.draggable ? "DRAG ENABLED" : "ENGINE MODE"}
          </span>
        </div>
        <svg
          viewBox="0 0 600 360"
          className="h-auto w-full rounded-2xl border border-slate-800 bg-slate-950 outline-none focus:ring-2 focus:ring-cyan-300"
          role={tools.draggable ? "application" : "img"}
          tabIndex={tools.draggable && !isSimulating ? 0 : undefined}
          aria-label={ariaLabel(instance, resultant, tools.coordinates)}
          onPointerDown={(event) => {
            if (!tools.draggable || isSimulating) return;
            const point = vectorFromPointer(event);
            if (point) updateVector(point);
          }}
          onPointerMove={(event) => {
            const point = vectorFromPointer(event);
            if (tools.hover) setHoverPoint(point);
            if (!point) return;
            if (!tools.draggable || isSimulating || event.buttons !== 1) return;
            updateVector(point);
          }}
          onPointerLeave={() => setHoverPoint(null)}
          onKeyDown={(event) => {
            if (!tools.draggable || isSimulating) return;
            handleVectorKey(event, { x, y }, updateVector);
          }}
        >
          <defs>
            <pattern id="vector-grid" width="25" height="25" patternUnits="userSpaceOnUse">
              <path d="M 25 0 L 0 0 0 25" fill="none" stroke="#1e293b" strokeWidth="1" />
            </pattern>
            <marker id="arrow-green" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="#34d399" />
            </marker>
            <marker id="arrow-orange" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="#fb923c" />
            </marker>
            <marker id="arrow-purple" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="#c084fc" />
            </marker>
          </defs>

          <rect width="600" height="360" fill="url(#vector-grid)" opacity={difficulty === "developer-mode" ? "0.35" : "1"} />
          {tools.coordinates && (
            <>
              <line x1={origin.x} y1="24" x2={origin.x} y2="336" stroke="#334155" strokeDasharray="5 5" />
              <line x1="25" y1={origin.y} x2="575" y2={origin.y} stroke="#334155" strokeDasharray="5 5" />
            </>
          )}
          {tools.gridLabels && <GridLabels />}

          <circle
            cx={target.x}
            cy={target.y}
            r={resolvedConfig.tolerance * scale}
            fill="rgba(34,211,238,.12)"
            stroke="#22d3ee"
            strokeWidth="4"
          />
          {tools.coordinates && (
            <text x={target.x} y={target.y - resolvedConfig.tolerance * scale - 10} textAnchor="middle" fill="#67e8f9" fontSize="11" fontWeight="800">
              EXTRACTION [{instance.target.x}, {instance.target.y}]
            </text>
          )}

          {tools.ruler && (
            <line x1={origin.x} y1={origin.y + 32} x2={target.x} y2={origin.y + 32} stroke="#67e8f9" strokeWidth="2" strokeDasharray="4 4" />
          )}
          {tools.protractor && <Protractor angle={inputAngle} />}
          {tools.componentTriangle && showVector && (
            <path
              d={`M ${origin.x} ${origin.y} L ${propulsionTip.x} ${origin.y} L ${propulsionTip.x} ${propulsionTip.y}`}
              fill="none"
              stroke="#64748b"
              strokeWidth="2"
              strokeDasharray="5 5"
            />
          )}
          {tools.ghost && (
            <line x1={landing.x} y1={landing.y} x2={target.x} y2={target.y} stroke="#94a3b8" strokeWidth="2" strokeDasharray="3 5" opacity="0.65" />
          )}

          {showVector && (
            <>
              <line x1={origin.x} y1={origin.y} x2={propulsionTip.x} y2={propulsionTip.y} stroke="#34d399" strokeWidth="5" markerEnd="url(#arrow-green)" />
              <line x1={propulsionTip.x} y1={propulsionTip.y} x2={landing.x} y2={landing.y} stroke="#fb923c" strokeWidth="5" markerEnd="url(#arrow-orange)" />
              <line x1={origin.x} y1={origin.y} x2={landing.x} y2={landing.y} stroke="#c084fc" strokeWidth="3" strokeDasharray="8 7" markerEnd="url(#arrow-purple)" />
            </>
          )}

          {tools.componentLabels && showVector && (
            <>
              <text x={(origin.x + propulsionTip.x) / 2} y={origin.y + 17} textAnchor="middle" fill="#34d399" fontSize="11" fontWeight="800">
                x {x}
              </text>
              <text x={propulsionTip.x + 12} y={(origin.y + propulsionTip.y) / 2} fill="#34d399" fontSize="11" fontWeight="800">
                y {y}
              </text>
            </>
          )}

          {isSimulating && (
            <line x1={origin.x} y1={origin.y} x2={dronePosition.x} y2={dronePosition.y} stroke="#e2e8f0" strokeWidth="2" strokeDasharray="4 5" />
          )}
          <g transform={`translate(${dronePosition.x} ${dronePosition.y})`}>
            <circle r="12" fill="#e2e8f0" stroke="#818cf8" strokeWidth="3" />
            <line x1="-20" x2="20" stroke="#818cf8" strokeWidth="4" />
            <circle cx="-18" r="4" fill="#22d3ee" />
            <circle cx="18" r="4" fill="#22d3ee" />
          </g>

          {tools.hover && hoverPoint && (
            <text x="28" y="348" fill="#94a3b8" fontSize="11">
              Hover vector [{Math.round(hoverPoint.x)}, {Math.round(hoverPoint.y)}]
            </text>
          )}
          {tools.coordinates && (
            <text x="28" y="30" fill="#94a3b8" fontSize="11">
              Origin [0, 0]
            </text>
          )}
        </svg>
      </Card>
    </div>
  );
}

function RangeControl({
  label,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange(value: number): void;
}) {
  const id = `vector-${label.toLowerCase().replaceAll(" ", "-")}`;
  return (
    <div>
      <label htmlFor={id} className="flex items-center justify-between text-xs font-bold text-slate-300">
        {label}
        <span className="font-retro text-[9px] text-emerald-300">{value}</span>
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : 0}
        disabled={disabled}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
        className="mt-3 w-full"
      />
      <div className="mt-2 flex justify-between text-[9px] text-slate-600">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

function NumberControl({
  label,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange(value: number): void;
}) {
  return (
    <label className="block text-xs font-bold text-slate-300">
      {label}
      <input
        type="number"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : 0}
        disabled={disabled}
        onChange={(event) => {
          if (event.target.value === "") return;
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
        className="mt-2 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
      />
    </label>
  );
}

function VectorReadout({
  label,
  x,
  y,
  color,
}: {
  label: string;
  x: number;
  y: number;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/65 p-3">
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className={`mt-1 font-retro text-[9px] ${color}`}>
        [{Math.round(x)}, {Math.round(y)}]
      </p>
    </div>
  );
}

function ScalarReadout({
  label,
  value,
  tone = "text-slate-200",
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/65 p-3">
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className={`mt-1 font-retro text-[9px] ${tone}`}>{value}</p>
    </div>
  );
}

function GridLabels() {
  return (
    <g fill="#475569" fontSize="9">
      {[0, 50, 100, 150, 200].map((x) => (
        <text key={x} x={origin.x + x * scale} y={origin.y + 14}>
          {x}
        </text>
      ))}
      {[0, 50, 100].map((y) => (
        <text key={y} x={origin.x - 24} y={origin.y - y * scale}>
          {y}
        </text>
      ))}
    </g>
  );
}

function Protractor({ angle }: { angle: number }) {
  const radius = 46;
  const end = {
    x: origin.x + Math.cos((angle * Math.PI) / 180) * radius,
    y: origin.y - Math.sin((angle * Math.PI) / 180) * radius,
  };
  return (
    <g>
      <path d={`M ${origin.x + radius} ${origin.y} A ${radius} ${radius} 0 0 0 ${end.x} ${end.y}`} fill="none" stroke="#fbbf24" strokeWidth="2" />
      <text x={origin.x + radius + 8} y={origin.y - 10} fill="#fbbf24" fontSize="10" fontWeight="800">
        {angle.toFixed(0)} deg
      </text>
    </g>
  );
}

function toSvg(point: Point) {
  return {
    x: origin.x + point.x * scale,
    y: origin.y - point.y * scale,
  };
}

function fromSvg(point: Point) {
  return {
    x: (point.x - origin.x) / scale,
    y: (origin.y - point.y) / scale,
  };
}

function vectorFromPointer(event: PointerEvent<SVGSVGElement>) {
  const rect = event.currentTarget.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const point = fromSvg({
    x: ((event.clientX - rect.left) / rect.width) * 600,
    y: ((event.clientY - rect.top) / rect.height) * 360,
  });
  return isFinitePoint(point) ? point : null;
}

function handleVectorKey(
  event: KeyboardEvent<SVGSVGElement>,
  current: Point,
  updateVector: (point: Point) => void,
) {
  const step = event.shiftKey ? 10 : 1;
  const next =
    event.key === "ArrowLeft"
      ? { x: current.x - step, y: current.y }
      : event.key === "ArrowRight"
        ? { x: current.x + step, y: current.y }
        : event.key === "ArrowUp"
          ? { x: current.x, y: current.y + step }
          : event.key === "ArrowDown"
            ? { x: current.x, y: current.y - step }
            : null;
  if (!next) return;
  event.preventDefault();
  updateVector(next);
}

function clampInput(value: number, config: VectorDroneConfig) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(config.inputMin, Math.min(config.inputMax, value));
}

function isFinitePoint(point: Point) {
  return Number.isFinite(point.x) && Number.isFinite(point.y);
}

function normalizeDegrees(value: number) {
  return ((value % 360) + 360) % 360;
}

function ariaLabel(
  instance: VectorDroneInstance,
  resultant: Point,
  revealCoordinates: boolean,
) {
  if (!revealCoordinates) {
    return "Vector drone flight monitor. Developer mode hides coordinate labels until submission.";
  }
  return `Vector diagram. Wind ${instance.wind.x}, ${instance.wind.y}. Current resultant ${resultant.x}, ${resultant.y}. Target ${instance.target.x}, ${instance.target.y}.`;
}

function runSimulation(
  setProgress: (progress: number | null) => void,
  onComplete: () => void,
) {
  const duration = 1100;
  const startedAt = window.performance.now();

  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    setProgress(progress);
    if (progress < 1) {
      window.requestAnimationFrame(tick);
      return;
    }
    window.setTimeout(() => {
      setProgress(null);
      onComplete();
    }, 180);
  };

  setProgress(0);
  window.requestAnimationFrame(tick);
}
